/**
 * Pure helpers for reasoning about a wePAY catalog diff.
 *
 * The authoritative diff is produced server-side by the `wepayCatalogPreview`
 * Cloud Function (see `functions/src/wepay/preview.ts`) — only the server
 * has the wePAY credentials + whitelisted IP. The client uses these helpers
 * to:
 *
 *   • Render the "Pending updates" badge count
 *   • Re-compute a diff locally if we ever switch to a client-side dry-run
 *     (currently unused — kept here because the brief asks for a pure fn)
 *   • Group / filter the structured diff for the SyncPreviewModal
 */

import type { CatalogDiff } from './wepayFunctions';

/**
 * Local catalog snapshot — what the BOS would read from Firestore. Currently
 * unused at runtime (server computes the diff) but exposed so a future
 * client-side dry-run mode can reuse the same comparator.
 */
export interface LocalGameDoc {
  gameKey: string;
  name: string;
}

export interface LocalPackageDoc {
  amount: number; // matches wePAY `sof_defined_amount`
  price: number;  // local sale price
}

export interface LocalCatalog {
  games: LocalGameDoc[];
  packages: Map<string, LocalPackageDoc[]>; // gameKey -> packages
}

/**
 * Remote catalog snapshot — one entry per `pay_to_company`/gameKey, built
 * from `fetchServiceInfo` responses.
 */
export interface RemoteGame {
  gameKey: string;
  serviceName: string;
  amounts: number[]; // sof_defined_amounts
}

export interface RemoteCatalog {
  games: RemoteGame[];
}

const PRICE_EPSILON = 0.001;

/**
 * Compute a catalog diff client-side. Currently a fallback in case we ever
 * want to preview without the server round-trip.
 */
export function computeCatalogDiff(
  local: LocalCatalog,
  remote: RemoteCatalog,
): CatalogDiff {
  const diff: CatalogDiff = {
    newGames: [],
    newPackages: [],
    priceChanges: [],
    removedPackages: [],
  };

  const localByKey = new Map<string, LocalGameDoc>();
  for (const g of local.games) localByKey.set(g.gameKey, g);

  const remoteKeys = new Set<string>();
  for (const r of remote.games) remoteKeys.add(r.gameKey);

  for (const r of remote.games) {
    const localGame = localByKey.get(r.gameKey);
    if (!localGame) {
      diff.newGames.push({
        gameKey: r.gameKey,
        serviceName: r.serviceName,
        amounts: r.amounts,
      });
      continue;
    }

    const localPkgs = local.packages.get(r.gameKey) ?? [];
    const localByAmount = new Map<number, LocalPackageDoc>();
    for (const p of localPkgs) localByAmount.set(p.amount, p);

    for (const amount of r.amounts) {
      const localPkg = localByAmount.get(amount);
      if (!localPkg) {
        diff.newPackages.push({
          gameKey: r.gameKey,
          gameName: localGame.name,
          amount,
          description: `${amount.toLocaleString('th-TH')} บาท`,
        });
        continue;
      }
      if (Math.abs(localPkg.price - amount) > PRICE_EPSILON) {
        diff.priceChanges.push({
          gameKey: r.gameKey,
          gameName: localGame.name,
          amount,
          oldPrice: localPkg.price,
          newPrice: amount,
        });
      }
    }

    const remoteAmountSet = new Set(r.amounts);
    for (const localPkg of localPkgs) {
      if (!remoteAmountSet.has(localPkg.amount)) {
        diff.removedPackages.push({
          gameKey: r.gameKey,
          gameName: localGame.name,
          amount: localPkg.amount,
        });
      }
    }
  }

  return diff;
}

/**
 * Total number of pending changes across all four diff buckets.
 */
export function diffTotal(diff: CatalogDiff): number {
  return (
    diff.newGames.length +
    diff.newPackages.length +
    diff.priceChanges.length +
    diff.removedPackages.length
  );
}

/**
 * Returns true when the diff is empty (no pending updates).
 */
export function isDiffEmpty(diff: CatalogDiff): boolean {
  return diffTotal(diff) === 0;
}

/**
 * Empty diff — useful for initial state.
 */
export function emptyDiff(): CatalogDiff {
  return {
    newGames: [],
    newPackages: [],
    priceChanges: [],
    removedPackages: [],
  };
}
